import React, { memo, useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import styled, { useTheme } from 'styled-components'
import { formatBR } from '~/packages/sentinel/visualization/utils/format'

const Wrapper = styled.div`
  flex: 1;
  position: relative;
  display: flex;

  svg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
  }
`

const ToolTipContainer = styled.div`
  position: fixed;
  min-width: 150px;
  background-color: white;
  padding: 12px;
  border-radius: 6px;
  box-shadow: 0 0px 15px rgba(0, 0, 0, 0.2);
  z-index: 99999;
  pointer-events: none;

  p {
    margin: 0;
  }
`

interface Series {
  color: string
  values: Array<number | null>
  fillColor?: string
}

interface BandsShaded {
  from: number
  to: number
  color: string
  opacity?: number
}

interface Bands {
  upper?: number
  lower?: number
  median?: number
  shaded?: BandsShaded
}

interface TimeSeriesLinearProps {
  series: Series[]
  bands?: Bands
  disableTooltip?: boolean
  disableYAxis?: boolean
  disablePoints?: boolean
  padding?: number
}

interface TooltipData {
  value: string
  seriesIndex: number
  pointIndex: number
}

interface MinMaxPoint {
  value: number
  index: number
}

function TimeSeriesPreview({
  series,
  bands,
  disableTooltip = false,
  disableYAxis = false,
  disablePoints = false,
  padding = 0,
}: TimeSeriesLinearProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null)
  const [minValue, setMinValue] = useState<MinMaxPoint | null>(null)
  const [maxValue, setMaxValue] = useState<MinMaxPoint | null>(null)
  const theme = useTheme()
  const bottomPadding = 1

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) {
      return
    }

    const updateDimensions = () => {
      const rect = wrapper.getBoundingClientRect()
      setDimensions({
        width: Math.ceil(rect.width),
        height: Math.ceil(rect.height),
      })
    }

    updateDimensions()
    window.addEventListener('resize', updateDimensions)

    return () => {
      window.removeEventListener('resize', updateDimensions)
    }
  }, [wrapperRef.current])

  useEffect(() => {
    const seriesValues = series.flatMap(s => s.values.filter(val => val !== null)) as number[]
    const bandValues: number[] = []
    if (bands) {
      if (typeof bands.upper === 'number' && Number.isFinite(bands.upper))
        bandValues.push(bands.upper)
      if (typeof bands.lower === 'number' && Number.isFinite(bands.lower))
        bandValues.push(bands.lower)
      if (typeof bands.median === 'number' && Number.isFinite(bands.median))
        bandValues.push(bands.median)
      if (bands.shaded) {
        if (typeof bands.shaded.from === 'number' && Number.isFinite(bands.shaded.from))
          bandValues.push(bands.shaded.from)
        if (typeof bands.shaded.to === 'number' && Number.isFinite(bands.shaded.to))
          bandValues.push(bands.shaded.to)
      }
    }
    const validValues = [...seriesValues, ...bandValues]
    const computedMax = Math.floor(Math.max(...validValues))
    const computedMin = Math.floor(Math.min(...validValues))

    if (computedMax === -Infinity || computedMin === Infinity) {
      return
    }

    setMaxValue({ value: computedMax, index: -1 })
    setMinValue({
      value: computedMin === computedMax ? 0 : computedMin,
      index: -1,
    })
  }, [series, bands])

  const calculateY = useCallback(
    (value: number | null) => {
      if (value === null || minValue === null || maxValue === null) {
        return null
      }
      const adjustedHeight = Math.max(0, dimensions.height - bottomPadding)

      return (
        adjustedHeight -
        ((Math.floor(value) - minValue.value) / (maxValue.value - minValue.value)) * adjustedHeight
      )
    },
    [dimensions.height, bottomPadding, minValue, maxValue],
  )

  const globalLength = useMemo(() => {
    const lengths = series.map(s => s.values.length)
    if (lengths.length === 0) return 0
    return Math.max(...lengths)
  }, [series])

  const { pointsPerSeries, paths, areaPathsPerSeries } = useMemo(() => {
    const adjustedWidth = dimensions.width - padding * 2
    const pointWidth = globalLength > 1 ? adjustedWidth / (globalLength - 1) : 0
    const baseY = Math.max(0, dimensions.height - bottomPadding)

    const computedPoints = series.map(s =>
      s.values.map((value, index) => {
        const y = calculateY(value)
        if (y === null) {
          return null
        }
        const x = globalLength === 1 ? dimensions.width / 2 : index * pointWidth + padding
        const adjustedY = y
        return { x, y: adjustedY }
      }),
    )

    const computedPaths = computedPoints.map(points => {
      let d = ''
      for (let i = 0; i < points.length; i++) {
        const point = points[i]
        if (!point) continue
        if (d === '' || i === 0 || points[i - 1] === null) {
          d += `M ${point.x} ${point.y}`
        } else {
          d += ` L ${point.x} ${point.y}`
        }
      }
      return d
    })

    const computedAreaPathsPerSeries = computedPoints.map(points => {
      const pathsForSeries: string[] = []
      let currentSegment: Array<{ x: number; y: number }> = []

      for (let i = 0; i < points.length; i++) {
        const point = points[i]
        if (point) {
          currentSegment.push(point)
        } else {
          if (currentSegment.length >= 2) {
            let d = `M ${currentSegment[0].x} ${currentSegment[0].y}`
            for (let j = 1; j < currentSegment.length; j++) {
              d += ` L ${currentSegment[j].x} ${currentSegment[j].y}`
            }
            const lastPoint = currentSegment[currentSegment.length - 1]
            const firstPoint = currentSegment[0]
            d += ` L ${lastPoint.x} ${baseY} L ${firstPoint.x} ${baseY} Z`
            pathsForSeries.push(d)
          }
          currentSegment = []
        }
      }

      if (currentSegment.length >= 2) {
        let d = `M ${currentSegment[0].x} ${currentSegment[0].y}`
        for (let j = 1; j < currentSegment.length; j++) {
          d += ` L ${currentSegment[j].x} ${currentSegment[j].y}`
        }
        const lastPoint = currentSegment[currentSegment.length - 1]
        const firstPoint = currentSegment[0]
        d += ` L ${lastPoint.x} ${baseY} L ${firstPoint.x} ${baseY} Z`
        pathsForSeries.push(d)
      }

      return pathsForSeries
    })

    return {
      pointsPerSeries: computedPoints,
      paths: computedPaths,
      areaPathsPerSeries: computedAreaPathsPerSeries,
    }
  }, [
    calculateY,
    series,
    dimensions.width,
    dimensions.height,
    padding,
    bottomPadding,
    globalLength,
  ])

  const handleMouseEnter = (seriesIndex: number, pointIndex: number) => {
    const v = series[seriesIndex]?.values[pointIndex]
    setTooltipData({
      seriesIndex,
      pointIndex,
      value: v !== null && v !== undefined ? formatBR(v as number) : 'Sem dados',
    })
  }
  const handleMouseLeave = () => setTooltipData(null)

  const tooltipPos = useMemo(() => {
    if (!tooltipData || !wrapperRef.current) return null
    const rect = wrapperRef.current.getBoundingClientRect()
    const p = pointsPerSeries[tooltipData.seriesIndex]?.[tooltipData.pointIndex]
    if (!p) return null
    const TW = 150,
      TH = 60
    let left = rect.left + p.x - TW / 2
    let top = rect.top + p.y - TH - 10
    left = Math.max(8, Math.min(left, window.innerWidth - TW - 8))
    top = Math.max(8, Math.min(top, window.innerHeight - TH - 8))
    return { left, top }
  }, [tooltipData, pointsPerSeries])

  const allNonNullValues = [
    ...((series.flatMap(s => s.values.filter(v => v !== null)) as number[]) ?? []),
    ...(bands
      ? ([bands.upper, bands.lower, bands.median, bands?.shaded?.from, bands?.shaded?.to].filter(
          v => typeof v === 'number' && Number.isFinite(v as number),
        ) as number[])
      : []),
  ]
  if (allNonNullValues.length === 0 || allNonNullValues.every(value => value === 0)) {
    return (
      <Wrapper ref={wrapperRef}>
        <p
          style={{
            margin: '0',
            alignSelf: 'center',
            width: '100%',
            textAlign: 'center',
          }}
        >
          Sem dados para este preview.
        </p>
      </Wrapper>
    )
  }

  return (
    <Wrapper ref={wrapperRef}>
      <svg viewBox={`0 0 ${dimensions.width} ${dimensions.height}`} preserveAspectRatio='none'>
        {bands && minValue && maxValue && (
          <React.Fragment>
            {bands.shaded &&
              typeof bands.shaded.from === 'number' &&
              typeof bands.shaded.to === 'number' &&
              (() => {
                const yFrom = calculateY(bands.shaded!.from)
                const yTo = calculateY(bands.shaded!.to)
                if (yFrom === null || yTo === null) return null
                const y = Math.min(yFrom, yTo)
                const height = Math.abs(yTo - yFrom)
                const x = padding
                const width = Math.max(0, dimensions.width - padding * 2)
                return (
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill={bands.shaded!.color}
                    opacity={
                      typeof bands.shaded!.opacity === 'number' ? bands.shaded!.opacity : 0.2
                    }
                  />
                )
              })()}
            {typeof bands.upper === 'number' &&
              (() => {
                const y = calculateY(bands.upper!)
                if (y === null) return null
                return (
                  <line
                    x1={padding}
                    x2={Math.max(0, dimensions.width - padding)}
                    y1={y}
                    y2={y}
                    stroke={theme.colors.darkText}
                    strokeWidth={1}
                    strokeDasharray='4 4'
                    opacity={0.5}
                  />
                )
              })()}
            {typeof bands.lower === 'number' &&
              (() => {
                const y = calculateY(bands.lower!)
                if (y === null) return null
                return (
                  <line
                    x1={padding}
                    x2={Math.max(0, dimensions.width - padding)}
                    y1={y}
                    y2={y}
                    stroke={theme.colors.darkText}
                    strokeWidth={1}
                    strokeDasharray='4 4'
                    opacity={0.5}
                  />
                )
              })()}
            {typeof bands.median === 'number' &&
              (() => {
                const y = calculateY(bands.median!)
                if (y === null) return null
                return (
                  <line
                    x1={padding}
                    x2={Math.max(0, dimensions.width - padding)}
                    y1={y}
                    y2={y}
                    stroke={theme.colors.darkText}
                    strokeWidth={1}
                    opacity={0.7}
                  />
                )
              })()}
          </React.Fragment>
        )}
        {paths.length > 0 && (
          <React.Fragment>
            {areaPathsPerSeries.map((areaPaths, seriesIndex) =>
              areaPaths.map((d, i) => (
                <path
                  key={`area-${seriesIndex}-${i}`}
                  d={d}
                  fill={series[seriesIndex].fillColor ?? 'transparent'}
                  stroke='none'
                  opacity={0.2}
                />
              )),
            )}
            {paths.map((d, seriesIndex) => (
              <path
                key={`path-${seriesIndex}`}
                d={d}
                stroke={series[seriesIndex].color}
                fill='none'
                strokeWidth={3}
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            ))}
            {pointsPerSeries.map((points, seriesIndex) =>
              points.map(
                (point, pointIndex) =>
                  point !== null && (
                    <circle
                      key={`point-${seriesIndex}-${pointIndex}`}
                      cx={point.x}
                      cy={point.y}
                      r={3}
                      fill={disablePoints ? 'transparent' : series[seriesIndex].color}
                      onMouseEnter={() => handleMouseEnter(seriesIndex, pointIndex)}
                      onMouseLeave={() => handleMouseLeave()}
                    />
                  ),
              ),
            )}
          </React.Fragment>
        )}
        {!disableYAxis && minValue && maxValue && (
          <React.Fragment>
            <line x1={-10} y1={2} x2={0} y2={2} stroke={'#000'} strokeWidth={1} />
            <line
              x1={-10}
              y1={dimensions.height - bottomPadding + 2}
              x2={0}
              y2={dimensions.height - bottomPadding + 2}
              stroke={'#000'}
              strokeWidth={1}
            />
            <line
              x1={-padding}
              y1={dimensions.height - bottomPadding + 1}
              x2={-padding}
              y2={3}
              stroke={'#000'}
              strokeWidth={1}
            />
            <text
              x={-14}
              y={dimensions.height - bottomPadding - 1}
              textAnchor='end'
              dominantBaseline='middle'
              fill={theme.colors.darkText}
              fontSize={12}
              overflow={'visible'}
            >
              {minValue.value}
            </text>
            <text
              x={-14}
              y={5}
              textAnchor='end'
              dominantBaseline='middle'
              fill={theme.colors.darkText}
              fontSize={12}
              overflow={'visible'}
            >
              {maxValue.value}
            </text>
          </React.Fragment>
        )}
      </svg>
      {!disableTooltip &&
        tooltipData &&
        tooltipPos &&
        createPortal(
          <ToolTipContainer style={{ left: tooltipPos.left, top: tooltipPos.top }}>
            <p>
              <strong>Valor:</strong> {tooltipData.value}
            </p>
          </ToolTipContainer>,
          document.body,
        )}
    </Wrapper>
  )
}

export default memo(TimeSeriesPreview)
